/*
Purpose: Final confirmation dialog before launching an analysis run.
Audience: Users verifying cost, model, and targets prior to execution.
What it does:
- Summarizes configuration and estimated cost
- Provides proceed/cancel controls
Interplay:
- Used by AnalysisConfigurationMenu/AdvancedAnalysisModal before createAnalysisOrder
*/
/**
 * ==================================================================================
 * ANALYSIS CONFIRMATION MODAL - PROMPT REVIEW & FINAL CONFIRMATION
 * ==================================================================================
 * 
 * PURPOSE:
 * This modal appears when users click "Launch Professional Analysis" to provide
 * a final confirmation step with full transparency about what the AI will do.
 * It shows the actual prompts, parameters, and costs before launching.
 * 
 * FUNCTION:
 * - Displays the exact prompt that will be sent to the AI model
 * - Shows model parameters and configuration details
 * - Provides cost breakdown and analysis details
 * - Offers final confirmation or cancellation
 * - Builds user trust through complete transparency
 * 
 * WHO USES THIS:
 * - Investigators confirming analysis before launch
 * - Anyone wanting to understand what the AI will actually do
 * - Budget-conscious users reviewing costs
 * - Legal teams validating AI analysis methodology
 * 
 * INTEGRATION:
 * Called from AnalysisConfigurationMenu when user clicks launch button.
 * Shows confirmation, then either cancels or proceeds with actual launch.
 * ==================================================================================
 */

import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { 
  FaRobot, 
  FaDollarSign, 
  FaClock, 
  FaEye, 
  FaTimes, 
  FaPlay,
  FaShieldAlt,
  FaCode
} from 'react-icons/fa';
import { buildPromptForModel, getModelConfig } from '../../config/aiModelsConfig';
import '../../styles/AnalysisConfirmationModal.css';

const AnalysisConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  analysisRequest, 
  evidence, 
  costEstimation,
  currentUser 
}) => {
  // NOTE: Default tab changed from 'prompt' to 'parameters' - change back to 'prompt' when restoring premium feature
  const [activeTab, setActiveTab] = useState('parameters');
  const [isConfirming, setIsConfirming] = useState(false);

  // Debug: Log when modal opens/closes
  useEffect(() => {
    console.log('🎭 AnalysisConfirmationModal isOpen prop:', isOpen, {
      analysisRequest,
      evidence: evidence?.id,
      costEstimation
    });
  }, [isOpen]);

  // Lock body scroll and ensure modal is centered when open
  useEffect(() => {
    if (isOpen) {
      console.log('🔒 Locking body scroll for confirmation modal');
      // Save current scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      
      // Prevent scrolling on body
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = `-${scrollX}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.overflow = 'hidden';
      
      // Also prevent scrolling on html element
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        // Restore scroll position when modal closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        
        // Restore scroll position
        window.scrollTo(scrollX, scrollY);
      };
    }
  }, [isOpen]);

  // Build configuration details for display (actualPrompt excluded for security - reserved for premium feature)
  const promptDetails = useMemo(() => {
    if (!analysisRequest) return null;

    try {
      // Get the model configuration from user selection
      const modelId = analysisRequest.selectedModel || 'gemini-2.5-flash-lite'; // Use selected model or fallback
      const modelConfig = getModelConfig(modelId);
      
      // NOTE: actualPrompt building removed from frontend - sensitive data not exposed to client
      // The backend will still build and use the prompt when processing the analysis
      
      /* ========== TO RESTORE AI PROMPT TAB (PREMIUM FEATURE) ==========
       * Uncomment these lines to re-enable prompt building:
       * 
       * const actualPrompt = buildPromptForModel(
       *   modelId,
       *   analysisRequest.targets,
       *   evidence.type || 'video',
       *   analysisRequest.customInstructions
       * );
       * 
       * Then add to return object:
       *   actualPrompt,
       *   promptLength: actualPrompt.length,
       *   estimatedTokens: Math.ceil(actualPrompt.length / 4)
       * =============================================================== */

      return {
        modelConfig,
        // actualPrompt: REMOVED - not sent to frontend for security
        targets: analysisRequest.targets,
        targetTiers: analysisRequest.targetTiers || {}, // Per-target tier selections
        customInstructions: analysisRequest.customInstructions,
        depth: analysisRequest.depth,
        scanTier: analysisRequest.scanTier,
        preferredService: analysisRequest.preferredService,
        // promptLength and estimatedTokens removed since we're not building the prompt
      };
    } catch (error) {
      console.error('Error building prompt details:', error);
      return null;
    }
  }, [analysisRequest, evidence]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  if (!isOpen || !promptDetails) return null;

  // Render modal using portal to ensure it's at the root level
  const modalContent = (
    <div className="confirmation-modal-overlay" onClick={onClose}>
      <div className="confirmation-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="confirmation-header">
          <div className="header-content">
            <FaRobot className="header-icon" />
            <div className="header-text">
              <h2>Confirm Professional Analysis</h2>
              <p>Review AI configuration before launching analysis</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Cost Summary Bar */}
        <div className="cost-summary-bar">
          <div className="cost-item">
            <FaDollarSign className="cost-icon" />
            <span className="cost-label">Estimated Cost:</span>
            <span className="cost-value">${costEstimation.estimatedCost}</span>
          </div>
          <div className="cost-item">
            <FaClock className="cost-icon" />
            <span className="cost-label">Model:</span>
            <span className="cost-value">{promptDetails.modelConfig.name} — {promptDetails.modelConfig.model_name}</span>
          </div>
          <div className="cost-item">
            <FaEye className="cost-icon" />
            <span className="cost-label">Targets:</span>
            <span className="cost-value">{promptDetails.targets.join(', ')}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="confirmation-tabs">
          {/* ========== TO RESTORE AI PROMPT TAB (PREMIUM FEATURE) ==========
           * Uncomment this button to show AI Prompt tab:
           * 
           * <button 
           *   className={`tab-button ${activeTab === 'prompt' ? 'active' : ''}`}
           *   onClick={() => setActiveTab('prompt')}
           * >
           *   <FaCode className="tab-icon" />
           *   AI Prompt
           * </button>
           * =============================================================== */}
          <button 
            className={`tab-button ${activeTab === 'parameters' ? 'active' : ''}`}
            onClick={() => setActiveTab('parameters')}
          >
            <FaShieldAlt className="tab-icon" />
            Parameters
          </button>
          <button 
            className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            <FaEye className="tab-icon" />
            Analysis Details
          </button>
        </div>

        {/* Tab Content */}
        <div className="confirmation-content">
          
          {/* ========== TO RESTORE AI PROMPT TAB (PREMIUM FEATURE) ==========
           * Uncomment this section to show AI Prompt content:
           * 
           * {activeTab === 'prompt' && (
           *   <div className="prompt-section">
           *     <div className="prompt-header">
           *       <h3>Actual AI Prompt</h3>
           *       <div className="prompt-stats">
           *         <span>{promptDetails.promptLength} characters</span>
           *         <span>~{promptDetails.estimatedTokens} tokens</span>
           *       </div>
           *     </div>
           *     
           *     <div className="prompt-preview">
           *       <div className="prompt-content">
           *         {promptDetails.actualPrompt}
           *       </div>
           *     </div>
           *
           *     <div className="prompt-explanation">
           *       <FaExclamationTriangle className="explanation-icon" />
           *       <p>
           *         <strong>This is the exact prompt</strong> that will be sent to the AI model. 
           *         It includes your selected analysis targets, custom instructions, and forensic 
           *         compliance requirements.
           *       </p>
           *     </div>
           *   </div>
           * )}
           * =============================================================== */}

          {/* Parameters Tab */}
          {activeTab === 'parameters' && (
            <div className="parameters-section">
              <h3>AI Model Configuration</h3>
              
              <div className="parameter-grid">
                <div className="parameter-card">
                  <h4>Model Selection</h4>
                  <div className="parameter-details">
                    <div className="parameter-row">
                      <span className="param-label">Model:</span>
                      <span className="param-value">{promptDetails.modelConfig.name}</span>
                    </div>
                    <div className="parameter-row">
                      <span className="param-label">Provider:</span>
                      <span className="param-value">{promptDetails.modelConfig.provider}</span>
                    </div>
                    <div className="parameter-row">
                      <span className="param-label">Accuracy:</span>
                      <span className="param-value">{promptDetails.modelConfig.accuracy}</span>
                    </div>
                  </div>
                </div>

                <div className="parameter-card">
                  <h4>Generation Parameters</h4>
                  <div className="parameter-details">
                    {Object.entries(promptDetails.modelConfig.parameters).map(([key, value]) => (
                      <div key={key} className="parameter-row">
                        <span className="param-label">{key}:</span>
                        <span className="param-value">
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="parameter-explanation">
                <h4>Why These Parameters?</h4>
                <ul>
                  <li><strong>Low temperature (0.1):</strong> Ensures consistent, factual responses without creative interpretation</li>
                  <li><strong>Safety filters disabled:</strong> Allows analysis of crime scene content, violence, and evidence</li>
                  <li><strong>High token limit (65,536):</strong> Enables comprehensive forensic documentation with full detail</li>
                  <li><strong>top_p (0.8) & top_k (40):</strong> Balanced sampling for accurate but natural language</li>
                </ul>
              </div>
              
              <div style={{ 
                marginTop: '16px',
                padding: '12px',
                background: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                fontSize: '0.85rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FaShieldAlt style={{ color: '#8b5cf6' }} />
                  <strong style={{ color: '#8b5cf6' }}>Forensic Standards</strong>
                </div>
                <p style={{ margin: 0, color: '#94a3b8', lineHeight: 1.5 }}>
                  These parameters are optimized for legal evidence analysis. Low temperature ensures reproducible results,
                  while disabled safety filters allow the AI to analyze potentially sensitive crime scene content without
                  censorship. All outputs include confidence scores for courtroom admissibility.
                </p>
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="details-section">
              <h3>Analysis Configuration</h3>
              
              <div className="detail-cards">
                <div className="detail-card">
                  <h4>Evidence Information</h4>
                  <div className="detail-content">
                    <div className="detail-row">
                      <span className="detail-label">File:</span>
                      <span className="detail-value">{evidence.fileName}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Type:</span>
                      <span className="detail-value">{evidence.type || 'Video'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Size:</span>
                      <span className="detail-value">{(evidence.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                  </div>
                </div>

                <div className="detail-card">
                  <h4>Analysis Targets & Detail Levels</h4>
                  <div className="detail-content">
                    <div className="target-list">
                      {promptDetails.targets.map(target => {
                        const tier = promptDetails.targetTiers[target] || 'professional';
                        const tierColors = {
                          standard: '#10b981',
                          professional: '#3b82f6', 
                          forensic: '#8b5cf6'
                        };
                        const tierLabels = {
                          standard: 'Standard',
                          professional: 'Professional',
                          forensic: 'Forensic'
                        };
                        return (
                          <div key={target} className="target-item" style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '6px',
                            marginBottom: '6px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FaEye className="target-icon" />
                              <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                {target.replace('_', ' ')}
                              </span>
                            </div>
                            <span style={{
                              background: tierColors[tier.toLowerCase()] + '20',
                              border: `1px solid ${tierColors[tier.toLowerCase()]}`,
                              color: tierColors[tier.toLowerCase()],
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '0.85rem',
                              fontWeight: 600
                            }}>
                              {tierLabels[tier.toLowerCase()] || tier}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ 
                      marginTop: '12px', 
                      padding: '8px', 
                      background: 'rgba(59, 130, 246, 0.1)',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      color: '#94a3b8'
                    }}>
                      <strong>Tier Levels:</strong><br/>
                      • <span style={{color: '#10b981'}}>Standard</span>: Quick assessment (~1,000 tokens)<br/>
                      • <span style={{color: '#3b82f6'}}>Professional</span>: Detailed investigation (~5,000 tokens)<br/>
                      • <span style={{color: '#8b5cf6'}}>Forensic</span>: Comprehensive legal analysis (~15,000+ tokens)
                    </div>
                  </div>
                </div>

                <div className="detail-card">
                  <h4>Execution Plan</h4>
                  <div className="detail-content">
                    <div className="detail-row">
                      <span className="detail-label">Scan Type:</span>
                      <span className="detail-value">
                        {promptDetails.scanTier === 'configured_scan' ? 'Configured Analysis' : 
                         promptDetails.scanTier === 'quick_scan' ? 'Quick Scan' : 
                         'Advanced Scan'}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Vertex AI Service:</span>
                      <span className="detail-value">
                        {(promptDetails.preferredService || '').includes('pro') ? 'Pro Service' : 
                         (promptDetails.preferredService || '').includes('flash') ? 'Flash Service' : 
                         'Standard Service'}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Model API Name:</span>
                      <span className="detail-value">{promptDetails.modelConfig.model_name}</span>
                    </div>
                  </div>
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '0.8rem', 
                    color: '#94a3b8',
                    fontStyle: 'italic'
                  }}>
                    The Vertex AI service tier (Pro/Flash/Standard) is automatically selected based on your chosen AI model for optimal performance.
                  </div>
                </div>

                <div className="detail-card">
                  <h4>Output & Documentation</h4>
                  <div className="detail-content">
                    <div className="detail-row">
                      <span className="detail-label">Analysis Depth:</span>
                      <span className="detail-value">{promptDetails.depth || 'Standard'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Expected Output:</span>
                      <span className="detail-value">V4 Target-Centric JSON</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Documentation:</span>
                      <span className="detail-value">Confidence scores & timestamps</span>
                    </div>
                  </div>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: '#94a3b8'
                  }}>
                    <strong style={{color: '#10b981'}}>What You'll Receive:</strong>
                    <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px' }}>
                      <li>Structured JSON analysis for each target</li>
                      <li>Attributes (what each target IS)</li>
                      <li>Activities (what each target DOES)</li>
                      <li>Relationships (how targets connect)</li>
                      <li>Master timeline with all events</li>
                      <li>Confidence scores for all detections</li>
                    </ul>
                  </div>
                </div>

                {promptDetails.customInstructions && (
                  <div className="detail-card">
                    <h4>Custom Instructions</h4>
                    <div className="detail-content">
                      <div className="custom-instructions">
                        {promptDetails.customInstructions}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="confirmation-footer">
          <div className="footer-info">
            <span>This analysis will be charged to your account</span>
          </div>
          
          <div className="footer-actions">
            <button 
              className="confirm-button" 
              onClick={handleConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <>
                  <FaRobot className="btn-icon spinning" />
                  Launching...
                </>
              ) : (
                <>
                  <FaPlay className="btn-icon" />
                  Confirm & Launch Analysis
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document root level, not inside parent modal
  return ReactDOM.createPortal(modalContent, document.body);
};

export default AnalysisConfirmationModal;